import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchSprintsByProject,
  startSprint,
  completeSprint,
  pauseSprint,
  resumeSprint,
  deleteSprint,
  Sprint,
  SprintStatus,
} from '../../../store/sprintSlice';

interface Props {
  projectId: string;
  onSelectSprint?: (sprintId: string) => void;
  onNewSprint?: () => void;
}

const statusColors: Record<SprintStatus, string> = {
  planned: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
};

const SprintList: React.FC<Props> = ({ projectId, onSelectSprint, onNewSprint }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sprints, loading, error } = useAppSelector(s => s.sprints);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<{ id: string; action: string } | null>(null);
  const [pauseReason, setPauseReason] = useState('');

  useEffect(() => {
    dispatch(fetchSprintsByProject({ projectId, status: statusFilter || undefined }));
  }, [dispatch, projectId, statusFilter]);

  const handleLifecycle = async (sprint: Sprint, action: string) => {
    switch (action) {
      case 'start':
        await dispatch(startSprint(sprint._id));
        break;
      case 'complete':
        await dispatch(completeSprint(sprint._id));
        break;
      case 'pause':
        await dispatch(pauseSprint({ id: sprint._id, reason: pauseReason }));
        setPauseReason('');
        break;
      case 'resume':
        await dispatch(resumeSprint({ id: sprint._id }));
        break;
      case 'delete':
        await dispatch(deleteSprint(sprint._id));
        break;
    }
    setConfirmAction(null);
  };

  const activeSprint = sprints.find(s => s.status === 'active');

  if (loading && sprints.length === 0) {
    return <div className="flex justify-center p-8"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded">{error}</div>}

      {/* Header — only shown when sprints exist */}
      {sprints.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {(['', 'planned', 'active', 'paused', 'completed'] as const).map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
          <button
            onClick={() => onNewSprint ? onNewSprint() : navigate(`/employee/projects/${projectId}/sprints/new`)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            + New Sprint
          </button>
        </div>
      )}

      {/* Active sprint banner */}
      {activeSprint && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">Active Sprint</span>
            <h3 className="font-semibold text-gray-900 mt-0.5">{activeSprint.name}</h3>
            {activeSprint.timeline.endDate && (
              <p className="text-sm text-gray-500 mt-0.5">
                Ends {new Date(activeSprint.timeline.endDate).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onSelectSprint ? onSelectSprint(activeSprint._id) : navigate(`/employee/projects/${projectId}/sprints/${activeSprint._id}`)}
              className="bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-green-700"
            >
              Open Board
            </button>
            <button
              onClick={() => setConfirmAction({ id: activeSprint._id, action: 'pause' })}
              className="bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-50"
            >
              Pause
            </button>
          </div>
        </div>
      )}

      {/* Sprint list */}
      {sprints.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
          <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No sprints yet</h2>
          <p className="text-sm text-gray-500 max-w-sm mb-8">Sprints help you organize work into focused time-boxed cycles. Create your first sprint to get started.</p>
          <button
            onClick={() => onNewSprint ? onNewSprint() : navigate(`/employee/projects/${projectId}/sprints/new`)}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors"
          >
            Create Sprint
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {sprints.map(sprint => (
            <div key={sprint._id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[sprint.status]}`}>
                      {sprint.status}
                    </span>
                    <span className="text-xs text-gray-400 uppercase">{sprint.sprintType}</span>
                  </div>
                  <h3
                    className="font-semibold text-gray-900 mt-1 cursor-pointer hover:text-blue-600 truncate"
                    onClick={() => onSelectSprint ? onSelectSprint(sprint._id) : navigate(`/employee/projects/${projectId}/sprints/${sprint._id}`)}
                  >
                    {sprint.name}
                  </h3>
                  {sprint.description && (
                    <p className="text-sm text-gray-500 mt-0.5 truncate">{sprint.description}</p>
                  )}
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    {sprint.timeline.startDate && (
                      <span>Start: {new Date(sprint.timeline.startDate).toLocaleDateString()}</span>
                    )}
                    {sprint.timeline.endDate && (
                      <span>End: {new Date(sprint.timeline.endDate).toLocaleDateString()}</span>
                    )}
                    {sprint.stats.totalTasks > 0 && (
                      <span>{sprint.stats.completedTasks}/{sprint.stats.totalTasks} tasks</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {sprint.status === 'planned' && (
                    <button
                      onClick={() => setConfirmAction({ id: sprint._id, action: 'start' })}
                      className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded hover:bg-green-100"
                    >
                      Start
                    </button>
                  )}
                  {sprint.status === 'paused' && (
                    <button
                      onClick={() => dispatch(resumeSprint({ id: sprint._id }))}
                      className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-1 rounded hover:bg-yellow-100"
                    >
                      Resume
                    </button>
                  )}
                  {sprint.status === 'active' && (
                    <button
                      onClick={() => setConfirmAction({ id: sprint._id, action: 'complete' })}
                      className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded hover:bg-blue-100"
                    >
                      Complete
                    </button>
                  )}
                  <button
                    onClick={() => onSelectSprint ? onSelectSprint(sprint._id) : navigate(`/employee/projects/${projectId}/sprints/${sprint._id}`)}
                    className="text-xs text-gray-500 border border-gray-200 px-2 py-1 rounded hover:bg-gray-50"
                  >
                    View
                  </button>
                  {sprint.status === 'planned' && (
                    <button
                      onClick={() => setConfirmAction({ id: sprint._id, action: 'delete' })}
                      className="text-xs text-red-500 border border-red-200 px-2 py-1 rounded hover:bg-red-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm modal */}
      {confirmAction && (
        <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-semibold text-gray-900 mb-2 capitalize">
              {confirmAction.action} Sprint?
            </h3>
            {confirmAction.action === 'pause' && (
              <input
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mt-2 mb-3"
                placeholder="Reason for pausing (optional)"
                value={pauseReason}
                onChange={e => setPauseReason(e.target.value)}
              />
            )}
            <p className="text-sm text-gray-500 mb-4">
              {confirmAction.action === 'delete'
                ? 'This will permanently delete the sprint.'
                : confirmAction.action === 'complete'
                ? 'Incomplete items will be moved to the backlog.'
                : `Are you sure you want to ${confirmAction.action} this sprint?`}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const sprint = sprints.find(s => s._id === confirmAction.id);
                  if (sprint) handleLifecycle(sprint, confirmAction.action);
                }}
                className={`px-4 py-2 text-sm text-white rounded ${
                  confirmAction.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SprintList;
